import { useMemo, useState } from "react";

import {
    DateTimePicker,
    DialogWrapper,
    LabelWrapper,
    StatColumn,
    StatProps,
    StatRawValue,
    TagTextAlert
} from "@bridgesplit/ui";
import { AppDialog, useAppDialog } from "app/utils";
import { BalanceDisplay, DialogHeader, TokenInputWithPrice, useBalanceChecker } from "app/components/common";
import { convertDateJsToUnix, convertUnixToDateJs, formatDate, Hidable } from "@bridgesplit/utils";
import { ParsedVaultRewardsScheduleExpanded } from "@bridgesplit/abf-react";

import { EditRewardsForm } from "../common/types";
import { ManagerEditRewardsCta } from "./ManagerEditRewardsCta";
import { ManagerViewProvider } from "../../ManagerViewContext";
export interface ManagerEditRewardsProps {
    form: EditRewardsForm;
    setForm: (form: EditRewardsForm) => void;
    schedule: ParsedVaultRewardsScheduleExpanded | undefined;
    setStep: (step: RewardStep) => void;
}

enum RewardStep {
    EditRewardsConfig = "EditRewardsConfig"
}

const initialEditForm: EditRewardsForm = {
    rewardToken: undefined,
    rewardAmount: undefined,
    rewardEndTime: null
};

const REWARD_STEPS = [RewardStep.EditRewardsConfig];

export function ManagerEditRewardsDialog() {
    const { getData } = useAppDialog();
    const data = getData(AppDialog.ManagerEditRewards);

    const [form, setForm] = useState<EditRewardsForm>({
        ...initialEditForm,
        rewardEndTime: convertUnixToDateJs(data?.schedule?.rewardEndTime),
        rewardToken: data?.schedule?.rewardMetadata
    });
    const [step, setStep] = useState<RewardStep>(RewardStep.EditRewardsConfig);

    const stepIndex = REWARD_STEPS.indexOf(step);
    const content = useMemo(() => {
        const formProps: ManagerEditRewardsProps = {
            form,
            setForm,
            schedule: data?.schedule,
            setStep
        };

        switch (step) {
            case RewardStep.EditRewardsConfig:
                return <EditRewardsConfig {...formProps} />;
            default:
                return null;
        }
    }, [form, data?.schedule, step]);

    return (
        <ManagerViewProvider vaultExpanded={data?.vaultExpanded}>
            <DialogWrapper>
                <DialogHeader header="Edit Rewards" description="Edit the schedule for the rewards" />
                {content}
                {stepIndex === REWARD_STEPS.length - 1 ? null : null}
            </DialogWrapper>
        </ManagerViewProvider>
    );
}

function EditRewardsConfig(props: ManagerEditRewardsProps) {
    const { form, setForm, setStep, schedule } = props;

    const { balance, insufficientBalance, isLoading } = useBalanceChecker({
        mint: form.rewardToken?.mint,
        amount: form.rewardAmount
    });

    return (
        <>
            <LabelWrapper label="Reward Token">
                <TokenInputWithPrice
                    selectToken={null}
                    metadata={form?.rewardToken}
                    value={form?.rewardAmount}
                    setValue={(val: number | undefined) => {
                        setForm({
                            ...form,
                            rewardAmount: val
                        });
                    }}
                    belowInput={
                        <BalanceDisplay
                            balance={balance}
                            insufficientBalance={insufficientBalance}
                            isLoading={isLoading}
                            setMax={(amount) => setForm({ ...form, rewardAmount: amount })}
                            metadata={form?.rewardToken}
                        />
                    }
                />
            </LabelWrapper>
            <LabelWrapper label="End Time">
                <DateTimePicker
                    value={form.rewardEndTime}
                    onChange={(val) => setForm({ ...form, rewardEndTime: val })}
                    sx={{
                        width: "100%"
                    }}
                />
            </LabelWrapper>
            <EditStats form={form} setForm={setForm} setStep={setStep} schedule={schedule} />
            <EditWarnings {...props} />
            <ManagerEditRewardsCta {...props} />
        </>
    );
}

function EditStats({ form, setForm, setStep, schedule }: ManagerEditRewardsProps) {
    // Early return if schedule or form not available
    if (!schedule || !form) {
        return null;
    }

    const totalEmissionAmount = schedule.totalEmissionsAmount;
    const emissionsClaimed = schedule.emissionsClaimed;

    const totalUndistributedRewards: [StatRawValue, StatRawValue] = [
        totalEmissionAmount - emissionsClaimed,
        totalEmissionAmount - emissionsClaimed + (form.rewardAmount || 0)
    ];

    const endTimeChange: [StatRawValue, StatRawValue] = [
        formatDate(schedule.rewardEndTime),
        form.rewardEndTime ? formatDate(convertDateJsToUnix(form.rewardEndTime)) : undefined
    ];

    const stats: Hidable<StatProps[]> = [
        {
            caption: "Undistributed rewards",
            value: totalUndistributedRewards,
            hide: !form.rewardAmount
        },
        {
            caption: "End time",
            value: endTimeChange,
            hide: !form.rewardEndTime
        }
    ].filter((stat) => !stat.hide);

    return <StatColumn stats={stats} loading={!schedule || !form} />;
}
function EditWarnings({ form, schedule }: ManagerEditRewardsProps) {
    if (!schedule || !form) {
        return null;
    }

    const newEndTime = convertDateJsToUnix(form.rewardEndTime);

    if (newEndTime && newEndTime < schedule.rewardEndTime) {
        return (
            <TagTextAlert icon="warning" color="warning">
                The new end time is earlier than the current end time. This will increase emissions per second.
            </TagTextAlert>
        );
    }

    return null;
}
